// Shared DISPLAY-ONLY number formatting for all NerdSuite calculators.
//
// SAFETY CONTRACT: the output of formatNum() is for display only. It may
// contain thousands separators (commas) and must NEVER be passed back to
// parseFloat/Number or any computation — a comma would silently truncate
// the value (parseFloat("1,234") === 1). All parsing stays on raw input
// strings; only final results are run through formatNum().
//
// Behavior:
//  - Groups the integer part in threes with commas: 1234567 -> "1,234,567"
//  - Leaves the fractional part ungrouped: 1234.5678 -> "1,234.5678"
//  - Preserves a leading minus: -1234 -> "-1,234"
//  - Very large / very small magnitudes fall back to exponential (no
//    misleading 20-digit comma strings), matching prior per-suite fmt().
//  - Non-finite (NaN/Infinity) -> "—" (same sentinel suites already used).
//  - Accepts a number OR a numeric string; non-numeric passes through
//    unchanged (so callers can hand it "undefined"/labels safely).

export function groupInt(intDigits) {
  // intDigits: a string of digits only (no sign, no decimal point).
  return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatNum(x, maxFrac = 6) {
  if (x === null || x === undefined || x === "") return "—";
  const n = typeof x === "number" ? x : Number(x);
  if (typeof n !== "number" || Number.isNaN(n)) {
    // Not numeric — return the original untouched (labels, etc.)
    return typeof x === "string" ? x : "—";
  }
  if (!Number.isFinite(n)) return "—";

  // Exponential fallback for magnitudes where grouping is meaningless or
  // misleading. Mirrors the thresholds the per-suite fmt() helpers used.
  if (n !== 0 && (Math.abs(n) >= 1e15 || Math.abs(n) < 1e-6)) {
    return n.toExponential(4);
  }

  const neg = n < 0;
  const abs = Math.abs(n);

  // Round to maxFrac decimals, then split — rounding first avoids a long
  // binary-float tail leaking into the displayed fraction.
  const fixed = abs.toFixed(maxFrac);
  let [intPart, fracPart = ""] = fixed.split(".");
  fracPart = fracPart.replace(/0+$/, ""); // trim trailing zeros

  const grouped = groupInt(intPart);
  const out = fracPart ? `${grouped}.${fracPart}` : grouped;
  return neg ? `-${out}` : out;
}

// Convenience: format then append a unit, skipping the unit when the
// value is the non-finite sentinel.
export function formatWithUnit(x, unit, maxFrac = 6) {
  const s = formatNum(x, maxFrac);
  return s === "—" || !unit ? s : `${s} ${unit}`;
}
